import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Plus, ReceiptText, Loader2, AlertTriangle } from 'lucide-react';
import Header from '../../components/Header';
import ReceiptViewer from '../../components/expenses/ReceiptViewer';
import { api } from '../../api/client';
import { formatCalendarDate } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import {
  parseExpenseList,
  getExpenseStatusLabel,
  getExpenseCategoryLabel,
  PAID_BY_LABELS,
  type Expense,
} from '../../lib/expenses';
import { useBack } from '../../hooks/useBack';

export const FIELD_EXPENSES_QUERY_KEY = ['field-expenses'] as const;

const statusStyles: Record<string, string> = {
  pending: 'bg-amber-500/20 text-amber-400',
  approved: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400',
};

/** Receipt dates are calendar days. @see formatCalendarDate. */
const fmtDay = (d: string | null) => formatCalendarDate(d, { month: 'short', day: 'numeric' }) ?? '—';

/**
 * A hand's own receipts, with where each one stands.
 *
 * Online-only by design (the brief for this build): React Query's cache is
 * in-memory, so the first open in a dead zone shows a loading state and then
 * an error, never a stale "nothing filed" — a read may degrade to an error, it
 * may not degrade to an empty state that reads as a fact.
 *
 * A COMPANY-CARD RECEIPT SAYS SO (0372). Approved, it has no pay period, so
 * the reimbursement line simply would not render — leaving it looking exactly
 * like an approved personal receipt whose period the payload did not carry.
 * The card names the path instead, from `payroll_path`.
 */
export default function FieldExpensesScreen() {
  const navigate = useNavigate();
  const goBack = useBack('/field');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: FIELD_EXPENSES_QUERY_KEY,
    queryFn: () => api.expenses.list().then((r) => parseExpenseList(r.data)),
  });
  const expenses: Expense[] = data ?? [];

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} className="text-fieldiq-text" aria-label="Back">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">Expenses</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        <button
          onClick={() => navigate('/field/expenses/new')}
          className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-xl p-3 flex items-center justify-center gap-2"
        >
          <Plus size={18} /> Add Receipt
        </button>

        {isLoading ? (
          <div className="text-center py-12 text-fieldiq-text flex items-center justify-center gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading expenses…
          </div>
        ) : isError ? (
          <div className="text-center py-12">
            <AlertTriangle size={40} className="mx-auto mb-3 text-fieldiq-danger" />
            <p className="text-fieldiq-text">Couldn't load your expenses.</p>
            <p className="text-xs text-fieldiq-muted mt-1">
              {(error as any)?.response?.data?.error || 'Check your connection and try again.'}
            </p>
          </div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-12">
            <ReceiptText size={40} className="mx-auto mb-3 text-fieldiq-muted" />
            <p className="text-fieldiq-text">No receipts filed yet</p>
            <p className="text-xs text-fieldiq-muted mt-1">Photograph a receipt on a job to claim it back.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {expenses.map((exp) => (
              <div
                key={exp.id}
                className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border"
                data-testid={`expense-${exp.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-white truncate">{exp.vendor ?? '—'}</span>
                    </div>
                    <p className="text-xs text-fieldiq-text mt-1">
                      {fmtDay(exp.expenseDate)}
                      {exp.jobNumber ? ` · ${exp.jobNumber}` : ''}
                      {' · '}
                      {getExpenseCategoryLabel(exp.category)}
                    </p>
                    {/* The hand's own note, rendered (0389) — it was captured
                        and shown nowhere, on this screen or the office's. On a
                        $0 company-paid fuel-card receipt this is where the
                        litres are, and the amount beside it reads $0.00; with
                        the note hidden the row says nothing at all. Wraps
                        rather than truncating: a half-shown quantity is worse
                        than none. */}
                    {exp.description && (
                      <p className="text-xs text-fieldiq-muted mt-1 whitespace-pre-line break-words">
                        {exp.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusStyles[exp.status] ?? 'bg-fieldiq-dark text-fieldiq-text'}`}>
                        {getExpenseStatusLabel(exp.status)}
                      </span>
                      {exp.paidBy === 'company' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-fieldiq-dark text-fieldiq-text">
                          {PAID_BY_LABELS.company}
                        </span>
                      )}
                      <ReceiptViewer uploadId={exp.receiptUploadId} />
                    </div>
                    {exp.status === 'rejected' && (
                      <p className="text-xs text-red-400 mt-2">
                        Rejected: {exp.rejectionReason ?? 'no reason given'}
                      </p>
                    )}
                    {/* The API's word first. 'none' is a result — the company
                        already paid — and printing nothing here would leave it
                        looking like a reimbursement that did not happen. */}
                    {exp.status === 'approved' && exp.payrollPath === 'none' ? (
                      <p className="text-xs text-fieldiq-muted mt-2">
                        Company card — recorded against the job for cost. Nothing is reimbursed to you.
                      </p>
                    ) : exp.status === 'approved' && exp.payPeriod && (exp.payPeriod.start || exp.payPeriod.end) ? (
                      <p className="text-xs text-green-400/80 mt-2">
                        On pay period {fmtDay(exp.payPeriod.start)} – {fmtDay(exp.payPeriod.end)}
                        {exp.carriedForward ? ' (carried forward)' : ''}
                      </p>
                    ) : null}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-white">{fmtCurrency(exp.amount)}</p>
                    <p className="text-[11px] text-fieldiq-muted mt-0.5">GST {fmtCurrency(exp.gst)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
